import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { ArrowLeft, GraduationCap, Loader2, Sparkles, Copy, Check } from "lucide-react";

const TARGETS = [
  { v: "global_school",    l: "Global School Teacher" },
  { v: "international",    l: "International Faculty" },
  { v: "india_school",     l: "India School-ready" },
  { v: "uae_school",       l: "UAE School-ready" },
  { v: "university",       l: "University Faculty" },
  { v: "activity_trainer", l: "Activity Trainer" },
  { v: "preschool",        l: "Preschool Educator" },
  { v: "executive",        l: "Executive Academic" },
];

export default function GrowthCvImprove() {
  const navigate = useNavigate();
  const [cvText, setCvText] = useState("");
  const [target, setTarget] = useState("global_school");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (cvText.trim().length < 80) {
      toast.error("Paste at least 80 characters of your CV to improve.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/growth/cv-improve", { cv_text: cvText, target, extra_context: extra || undefined });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not improve CV. Try again.");
      }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.content) return;
    try { await navigator.clipboard.writeText(result.content); setCopied(true); toast.success("Improved CV copied"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Improve My CV">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="cvimprove-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-amber inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <GraduationCap className="h-3 w-3" /> Improve My CV
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Reshape your CV for the right audience
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Paste your existing CV — TeeBee Intelligence rewrites it for the target school type, country and curriculum.
          Tighten the headline, quantify impact, sharpen the tone.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 tb-card p-5 self-start lg:sticky lg:top-6" data-testid="cvimprove-form">
          <p className="text-[11px] font-medium mb-1.5">Target context</p>
          <div className="flex flex-wrap gap-1.5" data-testid="cvimprove-targets">
            {TARGETS.map((t) => (
              <button
                key={t.v}
                onClick={() => setTarget(t.v)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  target === t.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                }`}
                data-testid={`cvimprove-target-${t.v}`}
              >{t.l}</button>
            ))}
          </div>

          <p className="text-[11px] font-medium mb-1.5 mt-4">Paste your CV text</p>
          <Textarea
            value={cvText}
            onChange={(e) => setCvText(e.target.value)}
            className="rounded-xl min-h-[260px] font-mono text-[12px] leading-relaxed"
            placeholder="Paste your full CV / resume content here…"
            data-testid="cvimprove-text"
          />

          <p className="text-[11px] font-medium mb-1.5 mt-4">Extra context (optional)</p>
          <Textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            className="rounded-xl min-h-[64px]"
            placeholder="e.g. moving to UAE after 5 years CBSE experience"
            data-testid="cvimprove-extra"
          />

          <div className="mt-3 flex items-center justify-between rounded-xl bg-muted/40 px-3 py-2 border border-border">
            <p className="text-xs">Exact cost: <span className="font-medium">5 coins</span></p>
            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">5 🪙</span>
          </div>

          <Button onClick={generate} disabled={busy} className="w-full h-11 rounded-full mt-3" data-testid="cvimprove-generate">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {busy ? "Polishing CV…" : "Improve my CV"}
          </Button>
        </div>

        <div className="lg:col-span-7" data-testid="cvimprove-result">
          {busy && <Skeleton className="h-72" />}
          {!busy && !result && (
            <div className="tb-card p-8 text-center">
              <Sparkles className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Paste your CV on the left and pick a target context. Your improved CV appears here.</p>
            </div>
          )}
          {result && (
            <div className="tb-card p-5 tb-fade-up">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Improved CV</p>
                <Button variant="ghost" size="sm" onClick={copy} className="h-7 px-2 text-xs">
                  {copied ? <Check className="h-3 w-3 mr-1 text-emerald-500" /> : <Copy className="h-3 w-3 mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <Markdown>{result.content}</Markdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
